import React, { useState, useEffect, useRef } from 'react';
import { authAPI } from '../../services/api';
import { ShieldCheckIcon, ShieldExclamationIcon, LockClosedIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

interface TwoFactorSettingsProps {
  onClose?: () => void;
}

const TwoFactorSettings: React.FC<TwoFactorSettingsProps> = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [enabledAt, setEnabledAt] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [password, setPassword] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [actionLoading, setActionLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const status = await authAPI.get2FAStatus();
      setTwoFactorEnabled(status.two_factor_enabled);
      setEnabledAt(status.enabled_at || null);
    } catch (err: any) {
      setError('Failed to fetch 2FA status');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtpDigits = [...otpDigits];
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newOtpDigits[index + i] = digit;
        }
      });
      setOtpDigits(newOtpDigits);
      const nextIndex = Math.min(index + digits.length, 5);
      otpInputRefs.current[nextIndex]?.focus();
    } else {
      const newOtpDigits = [...otpDigits];
      newOtpDigits[index] = value.replace(/\D/g, '');
      setOtpDigits(newOtpDigits);
      if (value && index < 5) {
        otpInputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleEnable = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setActionLoading(true);

    try {
      const result = await authAPI.enable2FA(password);
      setTwoFactorEnabled(result.two_factor_enabled);
      setEnabledAt(result.enabled_at || null);
      setSuccess('Two-factor authentication has been enabled successfully!');
      setShowEnableModal(false);
      setPassword('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to enable 2FA');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendCode = async () => {
    setSendingCode(true);
    try {
      await authAPI.send2FACode();
      setCodeSent(true);
      setSuccess('Verification code sent to your email');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setActionLoading(true);

    const otpCode = otpDigits.join('');
    if (otpCode.length !== 6) {
      setError('Please enter all 6 digits');
      setActionLoading(false);
      return;
    }

    try {
      const result = await authAPI.disable2FA(password, otpCode);
      setTwoFactorEnabled(result.two_factor_enabled);
      setEnabledAt(null);
      setSuccess('Two-factor authentication has been disabled');
      setShowDisableModal(false);
      setPassword('');
      setOtpDigits(['', '', '', '', '', '']);
      setCodeSent(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-volexx-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      <div className="bg-gradient-to-r from-volexx-blue-500 to-volexx-blue-600 p-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
            <ShieldCheckIcon className="h-7 w-7 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Two-Factor Authentication</h2>
            <p className="text-white/80 text-sm">Add an extra layer of security to your account</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {success}
          </div>
        )}

        <div className="flex items-center justify-between p-5 bg-gray-50 rounded-xl">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
              twoFactorEnabled 
                ? 'bg-green-100 text-green-600' 
                : 'bg-amber-100 text-amber-600'
            }`}>
              {twoFactorEnabled ? (
                <ShieldCheckIcon className="h-8 w-8" />
              ) : (
                <ShieldExclamationIcon className="h-8 w-8" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">
                {twoFactorEnabled ? '2FA Enabled' : '2FA Disabled'}
              </h3>
              <p className="text-sm text-gray-600">
                {twoFactorEnabled 
                  ? `Enabled on ${new Date(enabledAt!).toLocaleDateString()}`
                  : 'Your account is less secure without 2FA'
                }
              </p>
            </div>
          </div>
          
          <button
            onClick={() => twoFactorEnabled ? setShowDisableModal(true) : setShowEnableModal(true)}
            className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
              twoFactorEnabled
                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                : 'bg-volexx-blue-500 text-white hover:bg-volexx-blue-600'
            }`}
          >
            {twoFactorEnabled ? 'Disable 2FA' : 'Enable 2FA'}
          </button>
        </div>

        <div className="mt-6 p-5 border border-gray-200 rounded-xl">
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <EnvelopeIcon className="h-5 w-5 text-volexx-blue-500" />
            How Email 2FA Works
          </h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-volexx-blue-500 font-bold">1.</span>
              When you log in, we'll send a 6-digit code to your email
            </li>
            <li className="flex items-start gap-2">
              <span className="text-volexx-blue-500 font-bold">2.</span>
              Enter the code to complete your login
            </li>
            <li className="flex items-start gap-2">
              <span className="text-volexx-blue-500 font-bold">3.</span>
              Codes expire after 10 minutes for security
            </li>
          </ul>
        </div>
      </div>

      {showEnableModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-volexx-blue-100 flex items-center justify-center">
                <LockClosedIcon className="h-6 w-6 text-volexx-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Enable Two-Factor Authentication</h3>
                <p className="text-sm text-gray-600">Confirm your password to continue</p>
              </div>
            </div>

            <form onSubmit={handleEnable}>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500"
                  placeholder="Enter your password"
                  required
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEnableModal(false);
                    setPassword('');
                    setError('');
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading || !password}
                  className="flex-1 px-4 py-3 bg-volexx-blue-500 text-white rounded-lg hover:bg-volexx-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {actionLoading ? 'Enabling...' : 'Enable 2FA'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDisableModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <ShieldExclamationIcon className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Disable Two-Factor Authentication</h3>
                <p className="text-sm text-gray-600">This will make your account less secure</p>
              </div>
            </div>

            <form onSubmit={handleDisable}>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500"
                  placeholder="Enter your password"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code
                </label>
                {!codeSent ? (
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={sendingCode}
                    className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-volexx-blue-500 hover:text-volexx-blue-600 transition-colors"
                  >
                    {sendingCode ? 'Sending...' : 'Send verification code to email'}
                  </button>
                ) : (
                  <div className="flex justify-center gap-2">
                    {otpDigits.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => { otpInputRefs.current[index] = el; }}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className="w-10 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-volexx-blue-500 focus:ring-2 focus:ring-volexx-blue-500"
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDisableModal(false);
                    setPassword('');
                    setOtpDigits(['', '', '', '', '', '']);
                    setError('');
                    setCodeSent(false);
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading || !password || !codeSent || otpDigits.some(d => !d)}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {actionLoading ? 'Disabling...' : 'Disable 2FA'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwoFactorSettings;
